import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { USER_TYPE_ROUTES } from "@/components/security/user-type-access";
import type { RegisterUserType } from "@/domain/core";
import { AlertCircle, UserPlus, RefreshCw, LogIn } from "lucide-react";
import { useUserContext } from "@/context/user-context";
import { RegistrationWizard } from "./registration-wizard";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LoadingCard } from "@/components/ui/loading-card";
import { ModeToggle } from "@/components/ui/mode-toggle";
import { config } from "@/config/environment";

// ============================================================================
// USER REGISTRATION FLOW TYPES
// ============================================================================

export interface UserRegistrationFlowProps {
  children: React.ReactNode;
  onRegistrationComplete?: (userType?: RegisterUserType) => void;
  showRegistrationWizard?: boolean;
}

// ============================================================================
// REGISTRATION FLOW CARD COMPONENTS
// ============================================================================

/**
 * Component for API/Network errors (non-404 errors)
 */
function ApiErrorCard({
  onRetry,
  onRegister
}: {
  onRetry: () => void;
  onRegister: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-background flex items-center justify-center p-4 z-50">
      {/* Theme toggle in top-right corner */}
      <div className="absolute top-4 right-4 z-10">
        <ModeToggle />
      </div>
      <Card className="w-full max-w-md">
        <CardHeader className="gap-2">
          <div className="flex items-center justify-center space-x-2">
            <AlertCircle className="h-5 w-5 text-destructive" />
            <CardTitle className="text-destructive">Connection Error</CardTitle>
          </div>
          <CardDescription>
            Unable to connect to our servers. This could be due to network
            issues or server maintenance.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Please check your internet connection and try again. If the problem
            persists, you can still register a new account.
          </p>
          <div className="flex space-x-2">
            <Button variant="outline" onClick={onRetry} className="flex-1">
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
            <Button onClick={onRegister} className="flex-1">
              <UserPlus className="h-4 w-4 mr-2" />
              Register New Account
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Component for new user registration (registrationRequired: true)
 */
function NewUserRegistrationCard({
  onRegister,
  onSignOut
}: {
  onRegister: () => void;
  onSignOut: () => void;
  registrationMessage?: string;
}) {
  return (
    <div className="fixed inset-0 bg-background flex items-center justify-center p-4 z-50">
      {/* Theme toggle in top-right corner */}
      <div className="absolute top-4 right-4 z-10">
        <ModeToggle />
      </div>
      <Card className="w-full max-w-md">
        <CardHeader className="gap-2">
          <div className="flex items-center justify-center space-x-2">
            <UserPlus className="h-5 w-5 text-primary" />
            <CardTitle>Welcome to Tempii</CardTitle>
          </div>
          <CardDescription>
            {
              "We're excited to have you join our healthcare staffing platform. Let's get your account set up."
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {
              "Since this is your first time here, we need to collect some information to create your account. This will only take a few minutes."
            }
          </p>
          <Button onClick={onRegister} className="w-full" size="lg">
            <UserPlus className="h-4 w-4 mr-2" />
            Create My Account
          </Button>

          {/* Subtle sign-out option */}
          <div className="text-center">
            <button
              onClick={onSignOut}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors underline"
            >
              Sign out and use a different account
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Component for inactive user (403 Forbidden - User.Inactive)
 */
function InactiveUserCard({ onLogout }: { onLogout: () => void }) {
  return (
    <div className="fixed inset-0 bg-background flex items-center justify-center p-4 z-50">
      {/* Theme toggle in top-right corner */}
      <div className="absolute top-4 right-4 z-10">
        <ModeToggle />
      </div>
      <Card className="w-full max-w-md">
        <CardHeader className="gap-2">
          <div className="flex items-center justify-center space-x-2">
            <AlertCircle className="h-5 w-5 text-destructive" />
            <CardTitle className="text-destructive">Account Inactive</CardTitle>
          </div>
          <CardDescription>
            Your account has been deactivated and cannot be accessed at this
            time.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            If you believe this is an error or need to reactivate your account,
            please contact our support team. You can also try logging in with a
            different account by signing out and starting fresh.
          </p>
          <Button onClick={onLogout} className="w-full" size="lg">
            <LogIn className="h-4 w-4 mr-2" />
            Sign Out & Try Different Account
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Component for incomplete registration (user has ID but no profile data)
 */
function IncompleteRegistrationCard({
  onComplete
}: {
  onComplete: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-background flex items-center justify-center p-4 z-50">
      {/* Theme toggle in top-right corner */}
      <div className="absolute top-4 right-4 z-10">
        <ModeToggle />
      </div>
      <Card className="w-full max-w-md">
        <CardHeader className="gap-2">
          <div className="flex items-center justify-center space-x-2">
            <UserPlus className="h-5 w-5 text-primary" />
            <CardTitle>Complete Your Registration</CardTitle>
          </div>
          <CardDescription>
            We need some additional information to complete your account setup.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Your account was created but we need a bit more information to
            personalize your experience.
          </p>
          <Button onClick={onComplete} className="w-full" size="lg">
            <UserPlus className="h-4 w-4 mr-2" />
            Complete Registration
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// USER REGISTRATION FLOW COMPONENT
// ============================================================================

/**
 * UserRegistrationFlow - Manages the complete user registration and authentication flow
 *
 * This component handles:
 * - Loading states during user authentication
 * - Registration requirements and incomplete registrations
 * - Various user states (new user, inactive user, API errors)
 * - Registration wizard orchestration
 * - Error scenarios and recovery
 */
export function UserRegistrationFlow({
  children,
  onRegistrationComplete,
  showRegistrationWizard = true
}: UserRegistrationFlowProps) {
  const {
    isLoading,
    isError,
    isUserInactive,
    isServerError,
    user,
    userId,
    isRegistrationRequired,
    registrationMessage
  } = useUserContext();
  const { isAuthenticated, logoutAndRedirect } = useAuth();
  const [showRegistration, setShowRegistration] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Check if we're on the login page
  const isOnLoginPage = useMemo(
    () => location.pathname.startsWith("/login"),
    [location.pathname]
  );

  // Manual retry function for when users want to retry failed requests
  const handleManualRetry = useCallback(() => {
    if (userId && isAuthenticated) {
      // Force a refetch of the user profile query
      window.location.reload();
    }
  }, [userId, isAuthenticated]);

  // Handle logout for inactive users - force complete sign out
  const handleInactiveUserLogout = useCallback(async () => {
    try {
      await logoutAndRedirect();
    } catch (error) {
      console.error("Logout failed:", error);
      // Fallback: force redirect to login
      window.location.href = "/login";
    }
  }, [logoutAndRedirect]);

  const handleRegistrationComplete = useCallback(
    (userType?: RegisterUserType) => {
      setShowRegistration(false);
      if (userType === "professional" || userType === "practice") {
        navigate(USER_TYPE_ROUTES[userType], { replace: true });
      }
      onRegistrationComplete?.(userType);
    },
    [navigate, onRegistrationComplete]
  );

  // Handle registration cancel
  const handleRegistrationCancel = useCallback(() => {
    setShowRegistration(false);
  }, []);

  // Handle show registration
  const handleShowRegistration = useCallback(() => {
    setShowRegistration(true);
  }, []);

  // Check for any loading condition that should show LoadingCard
  const shouldShowLoading = useMemo(() => {
    return (
      isLoading || // General loading state
      (isAuthenticated && !userId) || // User authenticated but userId not yet available
      (isAuthenticated && !user && !isError && isOnLoginPage) || // User authenticated but no user data yet on login page
      (isAuthenticated && user && !user.registrationRequired && isOnLoginPage)
    ); // User authenticated with data but still on login page (redirecting)
  }, [isLoading, isAuthenticated, userId, user, isError, isOnLoginPage]);

  // CRITICAL: Never show login page components for authenticated users
  if (isAuthenticated) {
    // If user is authenticated and NOT on login page, check if registration is required
    if (!isOnLoginPage) {
      // If registration is required, show registration flow instead of dashboard
      if (isRegistrationRequired || showRegistration) {
        // Stay on the wizard until post-registration token refresh finishes.
        // Register mutations invalidate the user profile, which would otherwise
        // unmount the wizard and fire dashboard calls with the cached AT.
      } else {
        // User is fully registered, render dashboard content
        return <>{children}</>;
      }
    }
    // Continue to authenticated user logic below for login page or registration required
  } else {
    // Only show login page for truly unauthenticated users
    return <>{children}</>;
  }

  // Debug logging for authenticated users
  if (config.debug.enabled) {
    console.log("🔍 UserRegistrationFlow: User authenticated", {
      isAuthenticated,
      userId,
      isLoading,
      isError,
      user: !!user,
      isRegistrationRequired,
      showRegistration
    });
  }

  if (shouldShowLoading && !showRegistration) {
    return (
      <LoadingCard spinnerSize="lg" fullScreen={true} showThemeToggle={true} />
    );
  }

  // Show registration wizard if explicitly requested
  if (showRegistrationWizard && showRegistration) {
    return (
      <RegistrationWizard
        onRegistrationComplete={handleRegistrationComplete}
        onCancel={handleRegistrationCancel}
      />
    );
  }

  // Handle different scenarios based on error and user state
  if (isError) {
    if (isUserInactive) {
      // Scenario 1: User inactive (403 Forbidden - User.Inactive)
      return <InactiveUserCard onLogout={handleInactiveUserLogout} />;
    } else if (isServerError) {
      // Scenario 2: Server error (5xx) - Show error with retry options
      return (
        <ApiErrorCard
          onRetry={handleManualRetry}
          onRegister={handleShowRegistration}
        />
      );
    } else {
      // Scenario 3: Other API/Network errors - Show error with retry options
      return (
        <ApiErrorCard
          onRetry={handleManualRetry}
          onRegister={handleShowRegistration}
        />
      );
    }
  }

  // Check for registration required from successful API response (200 OK)
  if (isRegistrationRequired) {
    // Scenario 4: User found but registration required (registrationRequired: true) - New user registration
    return (
      <NewUserRegistrationCard
        onRegister={handleShowRegistration}
        onSignOut={handleInactiveUserLogout}
        registrationMessage={registrationMessage}
      />
    );
  }

  // Scenario 5: User has ID but no profile data - Incomplete registration
  if (userId && !user && !isLoading && !isError) {
    return <IncompleteRegistrationCard onComplete={handleShowRegistration} />;
  }

  // Scenario 6: User is fully registered and loaded
  // Don't show login page components - let LoginRedirect component handle the redirect
  if (user && !user.registrationRequired) {
    return <></>;
  }

  // Fallback loading state (should rarely reach here)
  // Never show login page components for authenticated users
  if (isAuthenticated && isOnLoginPage) {
    if (config.debug.enabled) {
      console.warn(
        "⚠️ UserRegistrationFlow: Unexpected state - authenticated user reached fallback, showing loading"
      );
    }
    return (
      <LoadingCard spinnerSize="lg" fullScreen={true} showThemeToggle={true} />
    );
  }

  // Only render children for truly unauthenticated users
  return <>{children}</>;
}
